import { NavLink } from 'react-router-dom';
import { useAuthStore } from '../../stores/auth.store';
import { JobTetrisMark } from '../ui/JobTetrisMark';

const NAV_ITEMS = [
  { to: '/dashboard',  label: 'Dashboard',  icon: IconGrid },
  { to: '/campaigns',  label: 'Campaigns',  icon: IconSend },
  { to: '/audiences',  label: 'Audiences',  icon: IconUsers },
  { to: '/inbox',      label: 'Inbox',      icon: IconInbox },
  { to: '/templates',  label: 'Templates',  icon: IconFile },
  { to: '/forms',      label: 'Forms',      icon: IconFile },
  { to: '/surveys',    label: 'Surveys',    icon: IconClipboard },
  { to: '/reports',    label: 'Reports',    icon: IconBarChart },
];

export function Sidebar() {
  // Render the super-admin link only when the user is on the allowlist AND
  // not in a takeover session. The server enforces the real check on
  // /system/* — this is purely a UI affordance.
  const isSuperAdmin = useAuthStore((s) => s.isSuperAdmin);
  const isTakeover = useAuthStore((s) => s.isTakeover);
  const showRestricted = isSuperAdmin && !isTakeover;

  return (
    <aside style={{
      width: 'var(--sidebar-width)',
      flexShrink: 0,
      backgroundColor: 'var(--color-sidebar-bg)',
      borderRight: '1px solid var(--color-sidebar-border)',
      display: 'flex',
      flexDirection: 'column',
      height: '100vh',
      position: 'sticky',
      top: 0,
    }}>
      {/* Logo — JT icon + "JobTetris" / "Engage" stack, mirrors the main app
       *  branding so users see this is JobTetris with an Engage sub-product
       *  rather than a separate standalone tool. */}
      <div style={{
        padding: 'var(--space-5) var(--space-4)',
        borderBottom: '1px solid var(--color-border-default)',
        display: 'flex',
        alignItems: 'center',
        gap: 'var(--space-3)',
        height: 'var(--topbar-height)',
      }}>
        <JobTetrisMark size={28} decorative />
        <div style={{ display: 'flex', flexDirection: 'column', lineHeight: 1, gap: '2px' }}>
          <span style={{
            fontFamily: 'var(--font-sans)',
            fontSize: 'var(--text-lg)',
            fontWeight: 'var(--font-weight-bold)',
            color: 'var(--color-text-brand)',
            letterSpacing: 'var(--tracking-tight)',
          }}>
            JobTetris
          </span>
          <span style={{
            fontFamily: 'var(--font-sans)',
            fontSize: 'var(--text-xs)',
            fontWeight: 'var(--font-weight-semibold)',
            color: 'var(--color-text-muted)',
            letterSpacing: 'var(--tracking-caps)',
            textTransform: 'uppercase',
          }}>
            Engage
          </span>
        </div>
      </div>

      {/* Nav */}
      <nav style={{ flex: 1, padding: 'var(--space-3)', overflowY: 'auto' }}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-1)' }}>
          {NAV_ITEMS.map(({ to, label, icon: Icon }) => (
            <NavLink
              key={to}
              to={to}
              style={({ isActive }) => ({
                display: 'flex',
                alignItems: 'center',
                gap: 'var(--space-2)',
                height: 'var(--sidebar-item-height)',
                padding: '0 var(--sidebar-item-padding-x)',
                borderRadius: 'var(--sidebar-item-radius)',
                fontSize: 'var(--text-sm)',
                fontWeight: isActive ? 'var(--font-weight-semibold)' : 'var(--font-weight-medium)',
                color: isActive ? 'var(--color-sidebar-active-text)' : 'var(--color-sidebar-text)',
                backgroundColor: isActive ? 'var(--color-sidebar-active-bg)' : 'transparent',
                textDecoration: 'none',
                transition: 'background-color var(--transition-base), color var(--transition-base)',
              })}
            >
              {({ isActive }) => (
                <>
                  <Icon
                    size={16}
                    color={isActive ? 'var(--color-sidebar-active-text)' : 'var(--color-sidebar-text)'}
                  />
                  {label}
                </>
              )}
            </NavLink>
          ))}

          {showRestricted && (
            <NavLink
              to="/dashboard/restricted"
              style={({ isActive }) => ({
                marginTop: 'var(--space-2)',
                display: 'flex',
                alignItems: 'center',
                gap: 'var(--space-2)',
                height: 'var(--sidebar-item-height)',
                padding: '0 var(--sidebar-item-padding-x)',
                borderRadius: 'var(--sidebar-item-radius)',
                fontSize: 'var(--text-sm)',
                fontWeight: isActive ? 'var(--font-weight-semibold)' : 'var(--font-weight-medium)',
                color: isActive ? 'var(--color-warning)' : 'var(--color-warning)',
                backgroundColor: isActive ? 'var(--color-sidebar-active-bg)' : 'transparent',
                border: '1px dashed var(--color-warning)',
                textDecoration: 'none',
              })}
            >
              <IconShieldLock size={16} />
              Restricted
            </NavLink>
          )}
        </div>
      </nav>

      {/* Settings at bottom */}
      <div style={{
        padding: 'var(--space-3)',
        borderTop: '1px solid var(--color-border-default)',
        display: 'flex',
        flexDirection: 'column',
        gap: 'var(--space-1)',
      }}>
        <NavLink
          to="/settings"
          style={({ isActive }) => ({
            display: 'flex',
            alignItems: 'center',
            gap: 'var(--space-2)',
            height: 'var(--sidebar-item-height)',
            padding: '0 var(--sidebar-item-padding-x)',
            borderRadius: 'var(--sidebar-item-radius)',
            fontSize: 'var(--text-sm)',
            fontWeight: isActive ? 'var(--font-weight-semibold)' : 'var(--font-weight-medium)',
            color: isActive ? 'var(--color-sidebar-active-text)' : 'var(--color-sidebar-text)',
            backgroundColor: isActive ? 'var(--color-sidebar-active-bg)' : 'transparent',
            textDecoration: 'none',
            transition: 'background-color var(--transition-base), color var(--transition-base)',
          })}
        >
          {({ isActive }) => (
            <>
              <IconSettings
                size={16}
                color={isActive ? 'var(--color-sidebar-active-text)' : 'var(--color-sidebar-text)'}
              />
              Settings
            </>
          )}
        </NavLink>
      </div>
    </aside>
  );
}

/* ── Inline SVG Icons (Feather-style, MIT) ── */

function IconShieldLock({ size = 16, color = 'currentColor' }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
      <rect x="9" y="11" width="6" height="5" rx="1" />
      <path d="M10 11V9.5a2 2 0 0 1 4 0V11" />
    </svg>
  );
}

function IconGrid({ size = 16, color = 'currentColor' }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <rect x="3" y="3" width="7" height="7"/><rect x="14" y="3" width="7" height="7"/>
      <rect x="14" y="14" width="7" height="7"/><rect x="3" y="14" width="7" height="7"/>
    </svg>
  );
}

function IconSend({ size = 16, color = 'currentColor' }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <line x1="22" y1="2" x2="11" y2="13"/>
      <polygon points="22 2 15 22 11 13 2 9 22 2"/>
    </svg>
  );
}

function IconUsers({ size = 16, color = 'currentColor' }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/>
      <circle cx="9" cy="7" r="4"/>
      <path d="M23 21v-2a4 4 0 0 0-3-3.87"/>
      <path d="M16 3.13a4 4 0 0 1 0 7.75"/>
    </svg>
  );
}

function IconInbox({ size = 16, color = 'currentColor' }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <polyline points="22 12 16 12 14 15 10 15 8 12 2 12"/>
      <path d="M5.45 5.11L2 12v6a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2v-6l-3.45-6.89A2 2 0 0 0 16.76 4H7.24a2 2 0 0 0-1.79 1.11z"/>
    </svg>
  );
}

function IconFile({ size = 16, color = 'currentColor' }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
      <polyline points="14 2 14 8 20 8"/>
      <line x1="16" y1="13" x2="8" y2="13"/>
      <line x1="16" y1="17" x2="8" y2="17"/>
      <polyline points="10 9 9 9 8 9"/>
    </svg>
  );
}

function IconClipboard({ size = 16, color = 'currentColor' }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M9 2h6a1 1 0 0 1 1 1v1a1 1 0 0 1-1 1H9a1 1 0 0 1-1-1V3a1 1 0 0 1 1-1z"/>
      <path d="M8 4H6a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V6a2 2 0 0 0-2-2h-2"/>
      <line x1="9" y1="12" x2="15" y2="12"/>
      <line x1="9" y1="16" x2="13" y2="16"/>
    </svg>
  );
}

function IconBarChart({ size = 16, color = 'currentColor' }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <line x1="18" y1="20" x2="18" y2="10"/>
      <line x1="12" y1="20" x2="12" y2="4"/>
      <line x1="6" y1="20" x2="6" y2="14"/>
    </svg>
  );
}

function IconSettings({ size = 16, color = 'currentColor' }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="12" r="3"/>
      <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1 0 2.83 2 2 0 0 1-2.83 0l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-2 2 2 2 0 0 1-2-2v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83 0 2 2 0 0 1 0-2.83l.06-.06A1.65 1.65 0 0 0 4.68 15a1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1-2-2 2 2 0 0 1 2-2h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 0-2.83 2 2 0 0 1 2.83 0l.06.06A1.65 1.65 0 0 0 9 4.68a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 2-2 2 2 0 0 1 2 2v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 0 2 2 0 0 1 0 2.83l-.06.06A1.65 1.65 0 0 0 19.4 9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 2 2 2 2 0 0 1-2 2h-.09a1.65 1.65 0 0 0-1.51 1z"/>
    </svg>
  );
}
